// 逐頁欄位定義：把原系統實測到的表頭文字，轉成 fixtures 用得動的 Column[]。
//
// 為什麼分成兩個檔：
//   - legacy-table-headers.generated.ts 是**資料**（68 頁的表頭序列），由
//     scripts/extract-table-headers.ts 從 legacy raw DOM 產生，不手改；
//   - 這個檔是**規則**（中文標籤 → FieldKind），要人看得懂、要能被測試逐條驗。
//   把兩者放同一個檔的話，每次重跑產生器都會把規則一起蓋掉。
//
// ponytail: 沒有為 68 頁各寫一份 kind 對照，改成「一張有序規則表 + 一份標籤資料」。
// 規則表現在 12 條，涵蓋 230 個相異非空標籤裡的 204 條（88.7%）；命中不到的退回 'text'，
// 而不是硬編一份 230 列的字典——字典會在下一次盤點增加新頁面時立刻過期。
//
// FC-3：資料來源改為 legacy raw DOM（scripts/extract-table-headers.ts），
// 表頭是**順序敏感序列**，含空字串欄（原系統的操作欄）與重複文字。
// 68 頁共 489 格，其中 49 格是空字串欄（42 頁有）、6 頁有重複的表頭文字。
//
// 型別用 `import type` 從 fixtures.ts 取，是**型別匯入會被編譯器整段抹掉**，
// 不會產生 fixtures ↔ page-columns 的執行期循環相依（值的方向只有 fixtures → 這裡）。

import type { Column, FieldKind } from '@/mocks/fixtures';
import {
  LEGACY_TABLE_HEADERS,
  type LegacyHeaderCell,
} from '@/mocks/legacy-table-headers.generated';

export type { LegacyHeaderCell };

// ---------------------------------------------------------------------------
// status 欄位的值池
// ---------------------------------------------------------------------------
// 放在這裡而不是 fixtures.ts，是因為「哪一種狀態欄配哪一個池」是規則表的一部分
// （見 R03a/R03b/R03c）。fixtures.ts 的四種 PageShape 也從這裡取用同樣三個池。

export const STATUS_ENABLED: readonly string[] = ['啟用', '停用'];
export const STATUS_PROGRESS: readonly string[] = ['未開始', '進行中', '已完成', '已逾期'];
export const STATUS_REVIEW: readonly string[] = ['待審核', '審核通過', '退回補件'];

// ---------------------------------------------------------------------------
// 標籤 → FieldKind 的有序規則表
// ---------------------------------------------------------------------------
//
// 判定方式：由上往下，**第一個 pattern 命中的規則決定 kind**，全部沒中就是 'text'。
// 順序是規則的一部分，不能任意調換，理由寫在每條的 why 裡。
//
// 命中率（68 頁、230 個相異非空標籤、440 個非空表頭格）：
//   相異標籤 204/230 = 88.7%   表頭格 396/440 = 90.0%
// 沒命中的 26 個標籤大多是**動作欄**（操作／編輯／新增／下載／申請／明細／權限設定）
// 與**人員屬性欄**（職稱／電話／email／飲食）——現有 11 種 FieldKind 裡沒有對應的種類，
// 硬塞會比退回 'text' 更難解釋。要補的話是加 FieldKind，不是往規則表塞例外。

export interface KindRule {
  /** 規則編號，測試與說明文件用它引用。 */
  readonly id: string;
  readonly kind: FieldKind;
  /** 為什麼是這個 kind、為什麼排在這個位置。 */
  readonly why: string;
  readonly pattern: RegExp;
  /** kind === 'status' 時要用哪一個值池。 */
  readonly pool?: readonly string[];
}

export const KIND_RULES: readonly KindRule[] = [
  {
    id: 'R01',
    kind: 'endDate',
    why: '「應完成日／有效日期／起迄」是區間的結束端，必須排在一般日期規則前面，否則會被 R02 先吃掉。',
    pattern: /應完成|結束|有效日期|有效期限|迄/,
  },
  {
    id: 'R02',
    kind: 'date',
    why: '日期／時間／期間／年度／月份，以及「開始日」「發送日」這種以「日」結尾的欄。',
    pattern: /日期|時間|期間|年度|月份|日$/,
  },
  {
    id: 'R03a',
    kind: 'status',
    why: '審核類狀態（審核／通過／簽核／回覆）配「待審核／審核通過／退回補件」。排在 R03c 前面。',
    pattern: /審核狀態|通過狀態|簽核狀態|回覆狀態/,
    pool: STATUS_REVIEW,
  },
  {
    id: 'R03b',
    kind: 'status',
    why: '流程進度類狀態（表單／作答／登入／異動／公告）配「未開始／進行中／已完成／已逾期」。',
    pattern: /表單狀態|作答狀態|登入狀態|異動狀態|公告狀態/,
    pool: STATUS_PROGRESS,
  },
  {
    id: 'R03c',
    kind: 'status',
    why: '其餘含「狀態」或「是否」的欄一律當啟用／停用開關。',
    pattern: /狀態|是否/,
    pool: STATUS_ENABLED,
  },
  {
    id: 'R04',
    kind: 'score',
    why:
      '數量類：排序、時數、以「數」結尾的計數、率、完成度、分數與統計量（平均／最低／最高／總和）。' +
      '排在 R05 前面，「排序序號」才不會因為含「序號」被當成編號。',
    pattern: /^排序|時數|數量|數$|率$|完成度|分數|成績|平均|最低|最高|總和|例數|場次|人次|次數/,
  },
  {
    id: 'R05',
    kind: 'id',
    why: '編號類：代碼／編號／員編／學號／帳號／序號／NO./IP／簡碼。排在人員規則前面，「學員員編」才會是編號而不是人。',
    pattern: /代碼|編號|員編|學號|帳號|序號|^NO\.$|ip$|^IP$|簡碼/,
  },
  {
    id: 'R06',
    kind: 'module',
    why: '「來源／表單來源」指的是資料出自哪個模組。',
    pattern: /來源/,
  },
  {
    id: 'R07',
    kind: 'title',
    why:
      '會議名稱（研討會／討論會／會議／晨報會）是欄位標題本身，' +
      '必須排在 R08 前面——「各類醫事人員研討會」含「人員」，否則會被判成人員欄。',
    pattern: /研討會|討論會|會議|晨報會/,
  },
  {
    id: 'R08',
    kind: 'person',
    why: '人員類：姓名／教師／學員／導師／人員／醫師／學生／名冊／成員，以及以「者」結尾的角色稱謂。',
    pattern: /姓名|教師|學員|導師|人員|醫師|學生|留言者|名冊|成員|者$/,
  },
  {
    id: 'R09',
    kind: 'unit',
    why: '組織類：部門／科部／單位／醫院／組織／地點。排在 R11 後半的「名稱」之前，「部門名稱」才會給部門值。',
    pattern: /部門|科部|單位|醫院|組織|地點/,
  },
  {
    id: 'R10',
    kind: 'category',
    why: '分類類：類別／類型／用途／假別／角色／範圍／組別／階段。',
    pattern: /類別|類型|用途|假別|角色|範圍|組別|階段/,
  },
  {
    id: 'R11',
    kind: 'title',
    why: '主體名稱：名稱／主旨／主題／項目／目標／計畫／試卷／題庫／證照／案例／課程／留言／標題。',
    pattern: /名稱|主旨|主題|項目|目標|計畫|試卷|題庫|證照|案例|課程|留言|禁用語|標題/,
  },
  {
    id: 'R12',
    kind: 'text',
    why: '自由文字：說明／內容／備註／原因／標頭／方法／其他／標準／照片。明寫出來，好和「規則沒命中」分開統計。',
    pattern: /說明|內容|備註|原因|標頭|方法|其他|標準|照片/,
  },
];

/** 沒有任何規則命中時的 kind。 */
export const FALLBACK_KIND: FieldKind = 'text';

/** 命中的規則；沒有規則命中時回傳 null（呼叫端自行退回 FALLBACK_KIND）。 */
export function matchKindRule(label: string): KindRule | null {
  for (const rule of KIND_RULES) {
    if (rule.pattern.test(label)) {
      return rule;
    }
  }
  return null;
}

/** 標籤推斷出的 FieldKind。 */
export function inferKind(label: string): FieldKind {
  return matchKindRule(label)?.kind ?? FALLBACK_KIND;
}

// ---------------------------------------------------------------------------
// 表頭 → Column[]
// ---------------------------------------------------------------------------

/**
 * 欄位 key 由 kind 決定：同一頁裡第一個 date 欄是 `date`、第二個是 `date2`，依此類推。
 *
 * 不直接用中文標籤當 key，因為 key 會經 toUiKey() 變成 data-ui-id 的一段
 * （§10.1 規定 data-ui-id 只能是小寫英數與連字號），中文轉出來會整段被吃掉、
 * 同一頁的多個欄位撞成同一個 ID。以 kind + 序號組出來的 key 是純 ASCII、
 * 同一頁內唯一、而且只要表頭不變就永遠一樣。
 */
export function columnsFromHeaders(labels: readonly string[]): readonly Column[] {
  const used = new Map<FieldKind, number>();
  return labels.map((label) => {
    const rule = matchKindRule(label);
    const kind = rule?.kind ?? FALLBACK_KIND;
    const seen = used.get(kind) ?? 0;
    used.set(kind, seen + 1);
    const key = seen === 0 ? kind : `${kind}${String(seen + 1)}`;
    // exactOptionalPropertyTypes：pool 沒有值時必須整個不存在，不能塞 undefined。
    return rule?.pool === undefined ? { key, label, kind } : { key, label, kind, pool: rule.pool };
  });
}

// ---------------------------------------------------------------------------
// FC-3：表頭序列（含空字串欄）→ 表格欄位序列
// ---------------------------------------------------------------------------
//
// legacy-table-headers.generated.ts 現在是**順序敏感序列**，逐格照抄 raw DOM，
// 含空字串欄（原系統把操作連結放在那一欄）與重複的表頭文字。
// 這裡把它切成兩種東西：
//
//   1. `columnsFromCells()` → **資料欄**（只取非空表頭）。fixtures 依 Column.kind
//      產生每一列的值，所以資料欄一定要對應得到 row[key]（criteria C5-i）。
//   2. `legacyTableSequenceFor()` → **完整表格欄序列**（含空字串欄）。模板直接照它
//      畫 thead，**不得自行增刪或重排任何一欄**（criteria C1-b 的「模板不得自己補操作欄」）。
//
// 空字串欄的 key 用 `actions`／`actions2`…：四種 PageShape 都沒有叫 actions 的欄位，
// 不會與資料欄撞號；`Table` 用 `column.key` 當 React key，所以同頁多個空欄也要相異。
// 行動作只畫在**第一個** actions 欄（`primaryActions === true` 的那一格）。

/** 表格的一欄：資料欄有 `columnKey`，操作欄沒有。 */
export interface LegacyTableColumn {
  /** `Table` 的 column.key；操作欄是 `actions`／`actions2`…。 */
  readonly key: string;
  /** 表頭文字，逐字取自 raw DOM；操作欄通常是空字串。 */
  readonly header: string;
  /** 這一欄是原系統的操作欄（表頭為空字串）。 */
  readonly actions: boolean;
  /** 同一頁多個操作欄時，只有第一個要畫行動作按鈕。 */
  readonly primaryActions: boolean;
}

/** 非空表頭 → 資料欄。空字串欄不產生資料欄位（它是操作欄，沒有資料）。 */
export function columnsFromCells(cells: readonly LegacyHeaderCell[]): readonly Column[] {
  return columnsFromHeaders(cells.filter((cell) => cell.text !== '').map((cell) => cell.text));
}

/** 完整表格欄序列：與表頭序列**逐格一對一**，順序與空欄位置照抄。 */
export function tableSequenceFromCells(
  cells: readonly LegacyHeaderCell[],
): readonly LegacyTableColumn[] {
  const dataColumns = columnsFromCells(cells);
  let dataIndex = 0;
  let actionsIndex = 0;
  return cells.map((cell) => {
    if (cell.text === '') {
      actionsIndex += 1;
      const key = actionsIndex === 1 ? 'actions' : `actions${String(actionsIndex)}`;
      return { key, header: '', actions: true, primaryActions: actionsIndex === 1 };
    }
    const column = dataColumns[dataIndex];
    dataIndex += 1;
    return {
      key: column?.key ?? `col${String(dataIndex)}`,
      header: cell.text,
      actions: false,
      primaryActions: false,
    };
  });
}

/** pageId → 逐頁資料欄。同一個 pageId 只算一次。 */
const cache = new Map<string, readonly Column[]>();
/** pageId → 逐頁完整表格欄序列。 */
const sequenceCache = new Map<string, readonly LegacyTableColumn[]>();

/** 原系統實測到的表頭序列（含空字串欄）；該頁沒有選定表格時 undefined。 */
export function legacyHeaderCellsFor(pageId: string): readonly LegacyHeaderCell[] | undefined {
  return LEGACY_TABLE_HEADERS[pageId];
}

/**
 * 原系統實測到的逐頁**資料**欄位；該頁沒有表格時回傳 undefined，
 * 由 fixtures.columnsFor() 退回 PageShape 的四種通用欄位。
 */
export function legacyColumnsFor(pageId: string): readonly Column[] | undefined {
  const cached = cache.get(pageId);
  if (cached !== undefined) {
    return cached;
  }
  const cells = LEGACY_TABLE_HEADERS[pageId];
  if (cells === undefined) {
    return undefined;
  }
  const columns = columnsFromCells(cells);
  cache.set(pageId, columns);
  return columns;
}

/**
 * 原系統實測到的逐頁**完整表格欄序列**（含操作欄）；沒有表格時 undefined。
 * 模板照它畫 thead，一格都不增刪。
 */
export function legacyTableSequenceFor(pageId: string): readonly LegacyTableColumn[] | undefined {
  const cached = sequenceCache.get(pageId);
  if (cached !== undefined) {
    return cached;
  }
  const cells = LEGACY_TABLE_HEADERS[pageId];
  if (cells === undefined) {
    return undefined;
  }
  const sequence = tableSequenceFromCells(cells);
  sequenceCache.set(pageId, sequence);
  return sequence;
}
